import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { staffPca, staffScopes } from './msal';
import { setUnauthorizedHandler } from '../api/client';

// MSAL silent-refresh hangs indefinitely in edge cases (blocked third-party
// cookies, suspended tab, network flake). Cap it so the UI never freezes.
const MSAL_SILENT_TIMEOUT_MS = 5_000;
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    p.then((v) => { clearTimeout(timer); resolve(v); },
           (e) => { clearTimeout(timer); reject(e); });
  });
}

type Source = 'staff' | 'client';

interface ClientIdentity {
  id: string;
  email: string;
  name?: string;
}

interface AuthState {
  source: Source | null;
  staffAccount: AccountInfo | null;
  client: ClientIdentity | null;
  ready: boolean;
  getToken: () => Promise<string | null>; // only used for staff requests; clients use cookies
  signInStaff: () => Promise<void>;
  signInClient: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshClient: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

const API = '/api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [staffAccount, setStaffAccount] = useState<AccountInfo | null>(null);
  const [client, setClient] = useState<ClientIdentity | null>(null);
  const [source, setSource] = useState<Source | null>(null);
  const [ready, setReady] = useState(false);

  const refreshClient = async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (r.ok) {
        const data = (await r.json()) as ClientIdentity;
        setClient(data);
        setSource('client');
      } else {
        setClient(null);
      }
    } catch {
      setClient(null);
    }
  };

  // Guard so a 401 storm can't kick off multiple simultaneous redirects.
  const reauthInFlight = useRef(false);

  const startStaffReauth = async () => {
    if (reauthInFlight.current || !staffAccount) return;
    reauthInFlight.current = true;
    try {
      await staffPca.acquireTokenRedirect({ account: staffAccount, scopes: staffScopes });
    } catch {
      // If MSAL redirect itself fails, force sign-out so the user can start over.
      try { await staffPca.clearCache({ account: staffAccount }); } catch { /* ignore */ }
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await staffPca.initialize();
        const redirect = await staffPca.handleRedirectPromise();
        if (redirect?.account) {
          setStaffAccount(redirect.account);
          setSource('staff');
          // Best-effort audit of fresh staff sign-in (bounded so it can't
          // freeze the boot path).
          try {
            const tok = await withTimeout(
              staffPca.acquireTokenSilent({ account: redirect.account, scopes: staffScopes }),
              MSAL_SILENT_TIMEOUT_MS, 'MSAL silent (announce)');
            await fetch(`${API}/auth/staff/announce`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${tok.idToken}` },
            });
          } catch { /* ignore audit failures */ }
          return;
        }
        const existing = staffPca.getAllAccounts()[0];
        if (existing) {
          setStaffAccount(existing);
          setSource('staff');
          return;
        }
        // Not staff — check for a client session cookie
        await refreshClient();
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const getToken = async () => {
    if (source !== 'staff' || !staffAccount) return null;
    try {
      // MSAL's silent iframe hop can hang indefinitely if third-party
      // cookies are blocked or the tab was suspended — timeout keeps the
      // UI responsive and falls through to interactive re-auth.
      const r = await withTimeout(
        staffPca.acquireTokenSilent({ account: staffAccount, scopes: staffScopes }),
        MSAL_SILENT_TIMEOUT_MS, 'MSAL silent (getToken)');
      return r.idToken;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (/authority.*mismatch|client_info|invalid.*account/i.test(msg)) {
        try { await staffPca.clearCache({ account: staffAccount }); } catch { /* ignore */ }
        setStaffAccount(null);
        setSource(null);
        return null;
      }
      // Kick off interactive re-auth; return null so the current call fails
      // fast rather than blocking on the redirect.
      void startStaffReauth();
      return null;
    }
  };

  // Register a global 401 handler so any API call that comes back with 401
  // triggers the right re-auth path without every caller needing to catch it.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (source === 'staff' && staffAccount) {
        void startStaffReauth();
      } else {
        // External client (or unknown): send them to /login. Guard against
        // the /login page itself repeatedly triggering the handler.
        setClient(null);
        setSource(null);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    });
    return () => setUnauthorizedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, staffAccount]);

  const signInStaff = async () => {
    await staffPca.loginRedirect({ scopes: staffScopes });
  };

  const signInClient = async (email: string, password: string) => {
    const r = await fetch(`${API}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({ error: 'login_failed' }));
      throw new Error(body.error ?? 'login_failed');
    }
    await refreshClient();
  };

  const signOut = async () => {
    if (source === 'staff' && staffAccount) {
      await staffPca.logoutRedirect({ account: staffAccount });
      return;
    }
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    setClient(null);
    setSource(null);
    window.location.href = '/login';
  };

  return (
    <AuthCtx.Provider value={{ source, staffAccount, client, ready, getToken, signInStaff, signInClient, signOut, refreshClient }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth outside provider');
  return v;
}
